import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { helpdeskConfigAPI, teamsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Users,
  Settings,
  CheckCircle2,
  AlertCircle,
  Wrench,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const HelpdeskSettings = () => {
  const { isAdmin } = useAuth();
  const { canAccess, canWrite: canWritePerm } = usePermissions();
  const canView = isAdmin() || canAccess('mesa_de_ayuda', 'configuracion');
  const canWriteConfig = isAdmin() || canWritePerm('mesa_de_ayuda', 'configuracion');
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [itTeamId, setItTeamId] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedTeamDetails, setSelectedTeamDetails] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!canView) {
      toast.error('No tienes permisos para acceder a esta sección');
      navigate('/helpdesk');
      return;
    }
    fetchData();
  }, [canView, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await helpdeskConfigAPI.getITTeam();
      setItTeamId(res.data.it_team_id || '');
      setAvailableTeams(res.data.available_teams || []);
      
      // Derivar los detalles del equipo seleccionado desde available_teams
      // (evita llamar al endpoint admin-only /teams para miembros del Equipo TI)
      if (res.data.it_team_id) {
        const team = (res.data.available_teams || []).find(t => t.id === res.data.it_team_id);
        setSelectedTeamDetails(team || null);
      } else {
        setSelectedTeamDetails(null);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveITTeam = async () => {
    setSaving(true);
    try {
      await helpdeskConfigAPI.setITTeam(itTeamId);
      toast.success('Equipo TI configurado correctamente');
      fetchData();
    } catch (error) {
      console.error('Error saving IT team:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleResetTickets = async () => {
    if (resetConfirmText !== 'RESETEAR') return;
    setResetting(true);
    try {
      const res = await helpdeskConfigAPI.resetTickets();
      const d = res.data.deleted;
      toast.success(
        `Reset completado: ${d.tickets} tickets, ${d.history} historial, ${d.comments} comentarios, ${d.attachments} adjuntos, ${d.email_logs} logs eliminados`
      );
      setResetDialogOpen(false);
      setResetConfirmText('');
    } catch (error) {
      console.error('Error resetting tickets:', error);
      toast.error(error.response?.data?.detail || 'Error al resetear tickets');
    } finally {
      setResetting(false);
    }
  };

  if (!canView) {
    return null;
  }

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope']">Configuración de Mesa de Ayuda</h1>
            <p className="text-slate-500 text-sm">Configura el equipo de soporte técnico</p>
          </div>
        </div>

        {/* IT Team Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5" />
              Equipo de Soporte (TI)
            </CardTitle>
            <CardDescription>
              Define cuál equipo será responsable de atender los tickets de soporte.
              Los miembros de este equipo aparecerán como opciones al asignar tickets.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Selecciona el Equipo TI</label>
              <Select
                value={itTeamId || 'none'}
                onValueChange={(v) => setItTeamId(v === 'none' ? '' : v)}
                disabled={!canWriteConfig}
              >
                <SelectTrigger className="w-full max-w-md" data-testid="it-team-select">
                  <SelectValue placeholder="Selecciona un equipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin equipo asignado</SelectItem>
                  {availableTeams.map(team => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                Solo los equipos activos aparecen en esta lista
              </p>
            </div>

            {/* Selected Team Details */}
            {selectedTeamDetails && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">{selectedTeamDetails.name}</span>
                  <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Equipo TI Activo
                  </Badge>
                </div>
                <div className="text-sm text-slate-600">
                  <p><strong>Miembros:</strong> {selectedTeamDetails.member_count ?? selectedTeamDetails.member_ids?.length ?? 0} usuarios</p>
                </div>
              </div>
            )}

            {!itTeamId && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium">No hay equipo TI configurado</p>
                  <p>Sin un equipo configurado, no podrás asignar tickets a agentes específicos.</p>
                </div>
              </div>
            )}

            {canWriteConfig && (
              <Button 
                onClick={handleSaveITTeam} 
                disabled={saving}
                data-testid="save-it-team-btn"
              >
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Status Flow Documentation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Flujo de Estados
            </CardTitle>
            <CardDescription>
              Reglas de transición de estatus para tickets
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge className="bg-blue-100 text-blue-700">Abierto</Badge>
                <span>→</span>
                <Badge className="bg-amber-100 text-amber-700">En proceso</Badge>
                <span>→</span>
                <div className="flex items-center gap-2">
                  <Badge className="bg-slate-100 text-slate-700">En espera</Badge>
                  <span className="text-slate-400">ó</span>
                  <Badge className="bg-purple-100 text-purple-700">Programado</Badge>
                </div>
                <span>→</span>
                <Badge className="bg-amber-100 text-amber-700">En proceso</Badge>
                <span>→</span>
                <Badge className="bg-emerald-100 text-emerald-700">Cerrado</Badge>
              </div>
              <div className="mt-4 text-sm text-slate-600 space-y-1">
                <p>• <strong>Abierto → En proceso:</strong> Cuando se comienza a trabajar en el ticket</p>
                <p>• <strong>En proceso → En espera/Programado:</strong> Cuando se requiere información o se programa para después</p>
                <p>• <strong>En espera/Programado → En proceso:</strong> Cuando se retoma el trabajo</p>
                <p>• <strong>En proceso → Cerrado:</strong> Cuando se resuelve el ticket</p>
                <p className="text-amber-600 mt-2">⚠️ No se permite ir directamente de "Abierto" a "Cerrado"</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reset Tickets - Test Mode (Admin-only destructive action) */}
        {isAdmin() && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Resetear Tickets (Modo Pruebas)
            </CardTitle>
            <CardDescription>
              Elimina todos los tickets de prueba y reinicia las métricas de SLA a cero.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-700 space-y-1">
                <p className="font-medium">Esta acción es irreversible</p>
                <p>Se eliminarán permanentemente:</p>
                <ul className="list-disc list-inside ml-2 space-y-0.5">
                  <li>Todos los tickets</li>
                  <li>Historial de estados</li>
                  <li>Comentarios</li>
                  <li>Archivos adjuntos</li>
                  <li>Logs de email</li>
                </ul>
                <p className="mt-2">Las métricas de SLA se reiniciarán a 0. Las categorías y configuración de equipo no se verán afectadas.</p>
              </div>
            </div>
            <Button
              variant="destructive"
              onClick={() => setResetDialogOpen(true)}
              data-testid="reset-tickets-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Resetear Tickets y SLA
            </Button>
          </CardContent>
        </Card>
        )}

        {/* Reset Confirmation Dialog */}
        <Dialog open={resetDialogOpen} onOpenChange={(open) => { setResetDialogOpen(open); if (!open) setResetConfirmText(''); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-700">Confirmar Reset de Tickets</DialogTitle>
              <DialogDescription>
                Esta acción eliminará <strong>todos los tickets</strong> y reiniciará las métricas de SLA.
                No se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-slate-600">
                Escribe <strong className="text-red-600">RESETEAR</strong> para confirmar:
              </p>
              <Input
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="Escribe RESETEAR"
                data-testid="reset-confirm-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setResetDialogOpen(false); setResetConfirmText(''); }}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                disabled={resetConfirmText !== 'RESETEAR' || resetting}
                onClick={handleResetTickets}
                data-testid="reset-confirm-btn"
              >
                {resetting ? 'Eliminando...' : 'Confirmar Reset'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default HelpdeskSettings;
